import React, { useEffect } from "react";
import "./App.css";
import "./styles/Blog.css";

/* ------------------------------------------------------------------
   Blog post: the 50/50 payment plan
   Route: /blog/50-50-payment-appliance-repair
------------------------------------------------------------------- */
const PAGE_URL = "https://atappliancemaster.com/blog/50-50-payment-appliance-repair";
const PAGE_TITLE = "The 50/50 Payment Plan for Appliance Repair | AT Appliance Repair";
const PAGE_DESCRIPTION =
  "Pay 50% when we order your part and 50% once your appliance works. How our 50/50 payment plan works for appliance repair in Plainfield, Naperville & nearby.";
const PUBLISHED = "2026-10-29";

const PHONE_DISPLAY = "(331) 600-4935";
const PHONE_LINK = "tel:+13316004935";

/* FILL IN only what's true. Blank items stay hidden. */
const AUTHOR_NAME = "";        // e.g. "Ali T., owner" — shown under the title
const DEPOSIT_REFUND_POLICY = ""; // e.g. "If you cancel before we order the part, the full deposit is refunded."
const PAYMENT_METHODS = "";    // e.g. "cash, check, Zelle and all major credit cards"

const FAQS = [
  [
    "Do I pay anything before I approve the repair?",
    "Only the diagnostic: $79, or $99 for luxury brands. It covers the visit, a full inspection and a written quote. If you go ahead with the repair, the diagnostic fee is waived, so you pay only for the repair.",
  ],
  [
    "What does the 50% deposit pay for?",
    "It goes toward the part we order for your appliance. Paying it lets us order the exact part for your model right away.",
  ],
  [
    "When do I pay the other 50%?",
    "After the part is installed and your appliance is working. Our technician tests it with you before you pay the balance.",
  ],
  [
    "What if the repair can be done on the first visit?",
    "Then there is nothing to split. You pay once, after the repair is finished.",
  ],
  [
    "Is my repair still under warranty?",
    "Yes. Every repair includes a 30-day parts and labor warranty, and most luxury-brand repairs are covered for 90 days.",
  ],
  DEPOSIT_REFUND_POLICY ? ["Is the deposit refundable?", DEPOSIT_REFUND_POLICY] : null,
  PAYMENT_METHODS ? ["How can I pay?", `We accept ${PAYMENT_METHODS}.`] : null,
].filter(Boolean);

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: "The 50/50 Payment Plan: A Fair Way to Pay for Appliance Repair",
    description: PAGE_DESCRIPTION,
    datePublished: PUBLISHED,
    dateModified: PUBLISHED,
    mainEntityOfPage: PAGE_URL,
    image: "https://atappliancemaster.com/og-image.jpg",
    author: AUTHOR_NAME
      ? { "@type": "Person", name: AUTHOR_NAME }
      : { "@type": "Organization", name: "AT Appliance Repair and Maintenance" },
    publisher: { "@type": "Organization", name: "AT Appliance Repair and Maintenance" },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://atappliancemaster.com/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://atappliancemaster.com/blog" },
      { "@type": "ListItem", position: 3, name: "The 50/50 Payment Plan", item: PAGE_URL },
    ],
  },
];

export default function BlogFiftyFifty() {
  useEffect(() => {
    document.title = PAGE_TITLE;
    const setMeta = (selector, attr, key, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement(selector.startsWith("link") ? "link" : "meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute(selector.startsWith("link") ? "href" : "content", value);
    };
    setMeta('meta[name="description"]', "name", "description", PAGE_DESCRIPTION);
    setMeta('link[rel="canonical"]', "rel", "canonical", PAGE_URL);
  }, []);

  return (
    <main className="blog-post">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(SCHEMA) }} />

      <nav className="breadcrumb" aria-label="Breadcrumb">
        <a href="/">Home</a> / <a href="/blog">Blog</a> / <span>The 50/50 Payment Plan</span>
      </nav>

      <article>
        <header className="post-header">
          <p className="post-kicker">Pricing &amp; payments</p>
          <h1>The 50/50 Payment Plan: A Fair Way to Pay for Appliance Repair</h1>
          <p className="post-meta">
            {AUTHOR_NAME ? `By ${AUTHOR_NAME} · ` : "AT Appliance Repair and Maintenance · "}
            <time dateTime={PUBLISHED}>October 29, 2026</time> · 4 min read
          </p>
          <p className="post-lead">
            When your appliance repair needs a part, you pay 50% when we order it and the other 50% once your
            appliance is working again. You never pay the full price for work that isn't done, and we can order
            your part the same day you approve the repair.
          </p>
        </header>

        <div className="split-card" aria-label="How the 50/50 payment works">
          <div className="split-half">
            <span className="split-pct">50%</span>
            <span className="split-when">When you approve the repair</span>
            <span className="split-what">Goes toward your part, so we can order it right away</span>
          </div>
          <div className="split-half">
            <span className="split-pct">50%</span>
            <span className="split-when">When the repair is done</span>
            <span className="split-what">Paid only after your appliance is fixed and tested</span>
          </div>
        </div>

        <h2>Why appliance repair payments can feel risky</h2>
        <p>
          Many repairs can't be finished on the first visit. A refrigerator control board, a dryer heating
          element or a dishwasher pump often has to be ordered for your exact model, and parts for built-in
          brands like Sub-Zero, Wolf, Thermador and Miele can cost hundreds of dollars.
        </p>
        <p>
          That leaves two common payment setups, and neither one is great. If a company asks for everything
          upfront, you're trusting them to come back and finish the job. If a company asks for nothing, they
          carry the cost of an expensive special-order part that only fits your appliance. The 50/50 plan splits
          that risk down the middle.
        </p>

        <h2>How our 50/50 payment plan works</h2>
        <ol className="steps">
          <li>
            <strong>Diagnostic visit.</strong> A technician inspects your appliance, finds the problem and gives
            you a written quote for parts and labor. The visit is $79, or $99 for luxury brands like Sub-Zero and Wolf.
          </li>
          <li>
            <strong>You approve and pay 50%.</strong> If you decide to go ahead, you pay half of the quoted repair
            price. That deposit goes toward your part, and we order it. The diagnostic fee is waived.
          </li>
          <li>
            <strong>We install the part.</strong> When the part arrives, we come back and complete the repair.
          </li>
          <li>
            <strong>We test it with you, then you pay the rest.</strong> Once your appliance is running
            properly, you pay the remaining 50%.
          </li>
        </ol>

        <h2>An example</h2>
        <p>Here's how the numbers work for a sample repair quoted at $400 for parts and labor:</p>
        <table className="example-table">
          <thead>
            <tr>
              <th scope="col">Step</th>
              <th scope="col">You pay</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Diagnostic visit ($79, waived when you approve the repair)</td>
              <td>$0</td>
            </tr>
            <tr>
              <td>Deposit when you approve and we order the part</td>
              <td>$200</td>
            </tr>
            <tr>
              <td>Balance after the repair is finished and tested</td>
              <td>$200</td>
            </tr>
            <tr className="total-row">
              <td>Total</td>
              <td>$400</td>
            </tr>
          </tbody>
        </table>
        <p className="table-note">
          This is an example only. Your quote depends on your appliance, the part and the labor involved, and
          you'll always see the price before any work starts.
        </p>

        <h2>Why the 50/50 rule works for every customer</h2>
        <div className="two-col">
          <div>
            <h3>What you get</h3>
            <ul>
              <li>You only pay the second half after you see your appliance working</li>
              <li>Your money isn't tied up in work that hasn't happened yet</li>
              <li>A written price before anything is ordered</li>
              <li>A warranty on every repair: 30 days, or 90 days on most luxury-brand repairs</li>
            </ul>
          </div>
          <div>
            <h3>Why it helps us serve you faster</h3>
            <ul>
              <li>We can order your part the same day you say yes</li>
              <li>We can stock and order expensive parts for high-end brands without delay</li>
              <li>Clear, simple terms mean no surprises on either side</li>
            </ul>
          </div>
        </div>
        <p>
          It's the same idea behind most fair service agreements: both sides commit, and the final payment
          happens when the job is done right.
        </p>

        <h2>Questions to ask any appliance repair company about payment</h2>
        <p>Whoever you hire, these questions protect you:</p>
        <ul>
          <li>Will I get a written quote before you order parts or start work?</li>
          <li>How much do you need upfront, and what does it cover?</li>
          <li>When is the final payment due: before or after the repair is tested?</li>
          <li>Is the diagnostic fee credited if I go ahead with the repair?</li>
          <li>What warranty covers the parts and labor, and for how long?</li>
        </ul>
        <p>
          A company that pushes for 100% payment before ordering a part, or won't put a price in writing, is
          worth a second look.
        </p>

        <h2>Frequently asked questions</h2>
        <div className="post-faq">
          {FAQS.map(([q, a]) => (
            <div key={q}>
              <h3>{q}</h3>
              <p>{a}</p>
            </div>
          ))}
        </div>

        <aside className="post-cta">
          <h2>Need an appliance repaired?</h2>
          <p>
            We repair{" "}
            <a href="/services/refrigerator-repair">refrigerators</a>,{" "}
            <a href="/services/washer-repair">washers</a>,{" "}
            <a href="/services/dryer-repair">dryers</a>,{" "}
            <a href="/services/dishwasher-repair">dishwashers</a>,{" "}
            <a href="/services/oven-repair">ovens</a> and{" "}
            <a href="/services/stove-repair">ranges</a> in{" "}
            <a href="/illinois-service-area/appliance-repair-plainfield-illinois">Plainfield</a>,{" "}
            <a href="/illinois-service-area/appliance-repair-naperville-illinois">Naperville</a>,{" "}
            <a href="/illinois-service-area/appliance-repair-bolingbrook-illinois">Bolingbrook</a> and{" "}
            <a href="/serviceareas">nearby towns</a>. Same-day service is available in most cases.
          </p>
          <p>
            <a className="schedule-button" href={PHONE_LINK}>Call {PHONE_DISPLAY}</a>{" "}
            <a className="schedule-button cta-secondary" href="/contact">Schedule Service</a>
          </p>
        </aside>
      </article>

      <a className="mobile-call" href={PHONE_LINK}>Call {PHONE_DISPLAY}</a>
    </main>
  );
}